import { useState, useEffect, useRef, useCallback } from 'react'
import { getDataAPI, postDataAPI, getActiveArtistIds, getPrimaryArtistId } from '../lib/api'
import type {
  SectionGranularConfig,
  ShowsGranularConfig,
  MusicGranularConfig,
  ScheduleGranularConfig,
  OverviewGranularConfig,
  FinanceGranularConfig,
  TasksGranularConfig,
  KnowledgeGranularConfig,
  AccessLevel,
} from '../types/permissions'
import { getDefaultSectionConfig } from '../types/permissions'

// ─── Types ───────────────────────────────────────────────────────────

type UserPermissions = {
  sections: string[]
  sectionConfig: SectionGranularConfig
}

// ─── Legacy app → section mapping (still used for backends that only
//     return an `apps` array without granular config) ─────────────────

const APP_TO_SECTIONS: Record<string, string[]> = {
  bookings:   ['shows', 'schedule'],
  tourmngr:   ['shows', 'schedule'],
  catalog:    ['music'],
  knowledge:  ['knowledge'],
  workflows:  ['schedule'],
  financials: ['finance'],
  branddeals: ['deals'],
}

/**
 * Recursively search an object for an `apps` array (string[]).
 * Returns the first match found, or null.
 */
function findAppsArray(obj: any, depth = 0): string[] | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (Array.isArray(obj.apps) && obj.apps.every((a: any) => typeof a === 'string')) {
    return obj.apps
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findAppsArray(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Recursively search for a `sectionConfig` object from the API response */
function findSectionConfig(obj: any, depth = 0): SectionGranularConfig | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (obj.sectionConfig && typeof obj.sectionConfig === 'object') {
    return obj.sectionConfig as SectionGranularConfig
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findSectionConfig(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Recursively search for a `sections` array from the API response */
function findSectionsArray(obj: any, depth = 0): string[] | null {
  if (!obj || typeof obj !== 'object' || depth > 5) return null
  if (Array.isArray(obj.sections) && obj.sections.every((s: any) => typeof s === 'string')) {
    return obj.sections
  }
  for (const key of ['data', 'permissions', 'result', 'member', 'teamMember']) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findSectionsArray(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

/** Given the raw `apps` array, derive the set of allowed route sections. */
function deriveSections(apps: string[]): string[] {
  const sections = new Set<string>()
  sections.add('team')
  sections.add('settings')
  sections.add('schedule')
  for (const appId of apps) {
    const mapped = APP_TO_SECTIONS[appId]
    if (mapped) mapped.forEach((s) => sections.add(s))
  }
  return Array.from(sections)
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useUserPermissions() {
  // ── Synchronously resolve the fast paths (artist / no-user / no-artist)
  //    so ProtectedRoute never flashes "Loading..." for these cases. ──
  const { initPerms, initLoading, initIsTeamMember, initPrimaryArtistId, initUserData } = (() => {
    try {
      const user = localStorage.getItem('dizeeUser')
      const token = localStorage.getItem('dizeeToken')
      if (!user || !token) return { initPerms: null, initLoading: false, initIsTeamMember: false, initPrimaryArtistId: null, initUserData: null }
      const userData = JSON.parse(user)
      const isTM = ['manager', 'support', 'promoter'].includes(userData.professionRole)
      // Artists get full access immediately — no async work needed
      if (!isTM) return { initPerms: null, initLoading: false, initIsTeamMember: false, initPrimaryArtistId: null, initUserData: userData }
      const activeIds = getActiveArtistIds()
      // Team member with no active artists — base sections only (no activity until permissions are fetched)
      if (activeIds.length === 0) return { initPerms: { sections: ['team', 'settings', 'schedule'], sectionConfig: {} } as UserPermissions, initLoading: false, initIsTeamMember: true, initPrimaryArtistId: null, initUserData: userData }
      // Team member with active artists — fetch permissions for primary (first) artist
      return { initPerms: null, initLoading: true, initIsTeamMember: true, initPrimaryArtistId: activeIds[0], initUserData: userData }
    } catch {
      return { initPerms: null, initLoading: false, initIsTeamMember: false, initPrimaryArtistId: null, initUserData: null }
    }
  })()

  const [permissions, setPermissions] = useState<UserPermissions | null>(initPerms)
  const [loading, setLoading] = useState(initLoading)
  // Sync primary artist id so we re-fetch when user toggles artists on/off in Team Hub
  const [primaryArtistId, setPrimaryArtistId] = useState<string | null>(() => initPrimaryArtistId || getPrimaryArtistId())
  const userDataRef = useRef(initUserData)
  userDataRef.current = initUserData
  /** Team membership document id from API — `dizee:granularPerms` is often keyed by this, not User._id */
  const teamMembershipRecordIdRef = useRef<string | null>(null)

  useEffect(() => {
    const sync = () => setPrimaryArtistId(getPrimaryArtistId())
    sync()
    window.addEventListener('dizee:activeArtistsChanged', sync)
    return () => window.removeEventListener('dizee:activeArtistsChanged', sync)
  }, [])

  useEffect(() => {
    const primaryId = primaryArtistId
    const userData = userDataRef.current
    if (!initIsTeamMember || !primaryId || !userData) {
      if (initIsTeamMember && !primaryId) {
        setPermissions({ sections: ['team', 'settings', 'schedule'], sectionConfig: {} })
        setLoading(false)
      }
      return
    }

    setLoading(true)
    const fetchPermissions = async () => {
      try {
        const userData = userDataRef.current!
        const artistId = primaryId
        const role = (userData.professionRole === 'support'
          ? 'support'
          : userData.professionRole === 'promoter'
            ? 'promoter'
            : 'manager') as 'manager' | 'support' | 'promoter'

        // --- Attempt 1: POST team/member-permissions ---
        let apps: string[] | null = null
        let sections: string[] | null = null
        let sectionConfig: SectionGranularConfig | null = null
        let res: any = null

        try {
          res = await postDataAPI('team/member-permissions', {
            artistId,
            teamMemberEmail: userData.email,
          })
          console.log('[useUserPermissions] POST team/member-permissions response:', JSON.stringify(res))

          const mid =
            (res as { data?: { memberId?: string; data?: { memberId?: string } } })?.data?.memberId ??
            (res as { data?: { data?: { memberId?: string } } })?.data?.data?.memberId ??
            (res as { memberId?: string })?.memberId
          if (typeof mid === 'string' && mid) teamMembershipRecordIdRef.current = mid

          // Try to find the new granular format first
          sections = findSectionsArray(res)
          sectionConfig = findSectionConfig(res)

          // Fallback to legacy apps array
          if (!sections) {
            apps = findAppsArray(res)
          }
        } catch (err) {
          console.warn('[useUserPermissions] POST team/member-permissions failed:', err)
        }

        // --- Attempt 2 (fallback): GET team/member/:id ---
        if (!sections && !apps) {
          try {
            const memberId = res?.data?.memberId ?? res?.memberId
            if (memberId) {
              teamMembershipRecordIdRef.current = memberId
              const res2 = await getDataAPI(`team/member/${memberId}`)
              console.log('[useUserPermissions] GET team/member fallback response:', JSON.stringify(res2))
              sections = findSectionsArray(res2)
              sectionConfig = findSectionConfig(res2)
              if (!sections) apps = findAppsArray(res2)
            }
          } catch (err2) {
            console.warn('[useUserPermissions] GET team/member fallback failed:', err2)
          }
        }

        console.log('[useUserPermissions] resolved — sections:', sections, 'apps:', apps, 'sectionConfig:', sectionConfig)

        // --- Fallback: check localStorage for granular config saved by the artist ---
        // Keys may use User._id OR team membership record id (Team Management saves the latter).
        if (!sectionConfig) {
          try {
            const keys = [
              userData._id,
              teamMembershipRecordIdRef.current,
            ].filter((k): k is string => typeof k === 'string' && k.length > 0)
            for (const k of keys) {
              const localRaw = localStorage.getItem(`dizee:granularPerms:${artistId}:${k}`)
              if (localRaw) {
                sectionConfig = JSON.parse(localRaw) as SectionGranularConfig
                console.log('[useUserPermissions] loaded sectionConfig from localStorage fallback', k.slice(0, 8))
                break
              }
            }
          } catch { /* ignore */ }
        }

        // Deep-merge role defaults with backend sectionConfig so missing
        // sub-fields get populated while explicit backend overrides win.
        const defaults = getDefaultSectionConfig(role)
        const mergedSectionConfig: SectionGranularConfig = { ...defaults }
        if (sectionConfig && typeof sectionConfig === 'object') {
          for (const key of Object.keys(sectionConfig) as (keyof SectionGranularConfig)[]) {
            const backendVal = (sectionConfig as any)[key]
            const defaultVal = (defaults as any)[key]
            // Deep-merge sub-objects (e.g. 'team', 'shows') so individual fields are preserved
            if (backendVal && typeof backendVal === 'object' && !Array.isArray(backendVal) &&
                defaultVal && typeof defaultVal === 'object' && !Array.isArray(defaultVal)) {
              (mergedSectionConfig as any)[key] = { ...defaultVal, ...backendVal }
            } else {
              (mergedSectionConfig as any)[key] = backendVal
            }
          }
        }

        // Build final permissions — settings is always available; team only if backend granted it
        if (sections && sections.length > 0) {
          const finalSections = [...new Set(['settings', ...sections])]
          setPermissions({
            sections: finalSections,
            sectionConfig: mergedSectionConfig,
          })
        } else if (apps && apps.length > 0) {
          setPermissions({
            sections: deriveSections(apps),
            sectionConfig: mergedSectionConfig,
          })
        } else {
          console.warn('[useUserPermissions] Could not resolve permissions — granting role defaults')
          if (role === 'manager') {
            setPermissions({
              sections: ['team', 'settings', 'shows', 'schedule', 'music', 'links', 'knowledge'],
              sectionConfig: getDefaultSectionConfig('manager'),
            })
          } else if (role === 'promoter') {
            setPermissions({
              sections: ['team', 'settings', 'shows', 'schedule'],
              sectionConfig: getDefaultSectionConfig('promoter'),
            })
          } else {
            setPermissions({
              sections: ['team', 'settings', 'shows', 'schedule'],
              sectionConfig: getDefaultSectionConfig('support'),
            })
          }
        }
        
        setLoading(false)
      } catch (error) {
        console.error('[useUserPermissions] Unexpected error:', error)
        const ud = userDataRef.current
        const role = (ud?.professionRole === 'support'
          ? 'support'
          : ud?.professionRole === 'promoter'
            ? 'promoter'
            : 'manager') as 'manager' | 'support' | 'promoter'
        if (role === 'manager') {
          setPermissions({
            sections: ['team', 'settings', 'shows', 'schedule', 'music', 'links', 'knowledge'],
            sectionConfig: getDefaultSectionConfig('manager'),
          })
        } else if (role === 'promoter') {
          setPermissions({
            sections: ['team', 'settings', 'shows', 'schedule'],
            sectionConfig: getDefaultSectionConfig('promoter'),
          })
        } else {
          setPermissions({
            sections: ['team', 'settings', 'shows', 'schedule'],
            sectionConfig: getDefaultSectionConfig('support'),
          })
        }
        setLoading(false)
      }
    }

    fetchPermissions()
  }, [initIsTeamMember, primaryArtistId])

  // ─── Accessors ───────────────────────────────────────────────────

  /** Check if the user can visit a section at all */
  const hasPermission = (sectionId: string): boolean => {
    if (permissions === null) return true // artist = full access
    if (!permissions) return false
    return permissions.sections.includes(sectionId)
  }

  /** Get the granular config for a specific section (null = unrestricted / artist) */
  const getSectionConfig = <T = any>(sectionId: string): T | null => {
    if (permissions === null) return null // artist = no restrictions
    if (!permissions?.sectionConfig) return null
    return (permissions.sectionConfig as any)[sectionId] ?? null
  }

  /** Convenience: get Shows config */
  const getShowsConfig = (): ShowsGranularConfig | null => getSectionConfig<ShowsGranularConfig>('shows')

  /** Convenience: get Music config */
  const getMusicConfig = (): MusicGranularConfig | null => getSectionConfig<MusicGranularConfig>('music')

  /** Convenience: get Schedule config */
  const getScheduleConfig = (): ScheduleGranularConfig | null => getSectionConfig<ScheduleGranularConfig>('schedule')

  /** Convenience: get Overview config */
  const getOverviewConfig = (): OverviewGranularConfig | null => getSectionConfig<OverviewGranularConfig>('overview')

  /** Convenience: get Finance config */
  const getFinanceConfig = (): FinanceGranularConfig | null => getSectionConfig<FinanceGranularConfig>('finance')

  /** Convenience: get Tasks config */
  const getTasksConfig = (): TasksGranularConfig | null => getSectionConfig<TasksGranularConfig>('tasks')

  /** Convenience: get Knowledge config */
  const getKnowledgeConfig = (): KnowledgeGranularConfig | null => getSectionConfig<KnowledgeGranularConfig>('knowledge')

  /** Check if user is a team member (not an artist) */
  const isTeamMember = permissions !== null

  /** Check if a section is view-only for this user */
  const isViewOnly = (sectionId: string): boolean => {
    if (permissions === null) return false // artist = full access
    const config = getSectionConfig<{ accessLevel?: AccessLevel }>(sectionId)
    if (!config) return false
    return config.accessLevel === 'view'
  }

  /**
   * Whether monetary / financial detail should be hidden for this section (team members).
   * - overview, shows, music: granular `financialAccess` on that section.
   * - finance (Money page): need Finance section + overview financial opt-in.
   * - deals: dollar amounts / pipeline need Finance section + same overview opt-in (deals page stays visible).
   */
  const isFinancialHidden = (sectionId: string): boolean => {
    if (permissions === null) return false // artist = can see everything

    const overviewFinancialOff = (): boolean => {
      const ov = getSectionConfig<{ financialAccess?: boolean }>('overview')
      if (!ov) return true
      return ov.financialAccess !== true
    }

    if (sectionId === 'finance') {
      if (!hasPermission('finance')) return true
      return overviewFinancialOff()
    }

    if (sectionId === 'deals') {
      if (!hasPermission('finance')) return true
      return overviewFinancialOff()
    }

    const config = getSectionConfig<{ financialAccess?: boolean }>(sectionId)
    if (!config) return true // no config = gate financials
    return config.financialAccess !== true
  }

  /**
   * Deal pipeline / expected value / payment — per artist account.
   * Finance section + overview financial opt-in for that artist (localStorage for non-primary).
   */
  const isDealFinancialsHiddenForArtist = useCallback(
    (artistId: string | undefined): boolean => {
      if (permissions === null) return false
      if (!permissions.sections.includes('finance')) return true
      const aid = artistId || primaryArtistId || ''
      if (!aid) return true
      const overviewOff = (ov: { financialAccess?: boolean } | null | undefined) =>
        !ov || ov.financialAccess !== true

      const permOv = permissions.sectionConfig?.overview
      // `permissions` are loaded for `primaryArtistId` only. A grant on the primary account must not
      // expose deal/money amounts for other roster artists — those need their own overview grant (API or storage).
      if (aid === primaryArtistId && permOv?.financialAccess === true) return false

      // Same key Team Management uses — API merge can omit or default overview.financialAccess; storage is the artist-saved grant.
      try {
        const ud = userDataRef.current
        const keys = [ud?._id, teamMembershipRecordIdRef.current].filter(
          (k): k is string => typeof k === 'string' && k.length > 0,
        )
        for (const k of keys) {
          const raw = localStorage.getItem(`dizee:granularPerms:${aid}:${k}`)
          if (raw) {
            const cfg = JSON.parse(raw) as SectionGranularConfig
            if (cfg.overview?.financialAccess === true) return false
          }
        }
      } catch {
        /* ignore */
      }

      if (aid === primaryArtistId) {
        return overviewOff(permOv)
      }
      return true
    },
    [permissions, primaryArtistId],
  )

  return {
    permissions,
    loading,
    isTeamMember,
    hasPermission,
    isViewOnly,
    isFinancialHidden,
    isDealFinancialsHiddenForArtist,
    getSectionConfig,
    getShowsConfig,
    getMusicConfig,
    getScheduleConfig,
    getOverviewConfig,
    getFinanceConfig,
    getTasksConfig,
    getKnowledgeConfig,
  }
}
